import { isEmpty } from '@/types';
import { currencyFormat, dateFormat, isDateNewerThan } from '@/utils';
import { IconState } from '@/components';
import { JSONStorage } from '../utils/storage';
import { DataPoint, NumericField, Mode } from '@/features/insights';

export const euros = currencyFormat('EUR', 'es-ES');
export const shortEuros = currencyFormat('EUR', 'es-ES', 'short', 1, 1);
export const shortDate = dateFormat('DD-MM-YYYY');
export const timeFormat = dateFormat('hh:mm');
export const displayDate = dateFormat('MMM DD, YYYY');
export const isRecentlySynced = isDateNewerThan({ hours: 6 });

export const watts = (value?: number | null) => (value ? `${value.toFixed(1)} W` : null);
export const meters = (value?: number | null) => (value ? `${value.toFixed(2)} m` : null);
export const hours = (value?: number | null) => (value ? `${value.toFixed(2)} h` : null);
export const mw = (value?: number | null) => (value ? `${value.toFixed(1)} MW` : null);
export const mwp = (value?: number | null) => (value ? `${value.toFixed(1)} MWp` : null);
export const mwn = (value?: number | null) => (value ? `${value.toFixed(1)} MWn` : null);
export const mwh = (value?: number | null) => (value ? `${value.toFixed(1)} MWh` : null);
export const mwhmwp = (value?: number | null) => (value ? `${value.toFixed(0)} h (MWh/MWp)` : null);
export const mva = (value?: number | null) => (value ? `${value.toFixed(1)} (MVa/MWh)` : null);
export const kv = (value?: number | null) => (value ? `${value.toFixed(1)} kV` : null);

export const eurosMW = (value?: number | any | null) => (value ? `${euros(value)} €/MW` : null);
export const eurosMWp = (value?: number | any | null) => (value ? `${euros(value)} €/MWp` : null);

export const years = (value?: number | null) => (value ? `${value.toFixed(0)} years` : null);
export const weeks = (value?: number | null) => (value ? `${value.toFixed(0)} weeks` : null);

export const km = (value?: number | null) => (value ? `${value.toFixed()} km` : null);
export const ha = (value?: number | null) => (value ? `${customRound(value)} ha` : null);
export const eurosPerHa = (value?: number | null) => (value ? `${euros(roundEurosHa(value))} €/ha` : null);

export const percentage = (value?: number | null) => (value ? `${value.toFixed(2)}%` : null);
export const dateToMonth = (date?: string | number | null) => (date ? new Date(date).toISOString().slice(0, 7) : null);

export const sessionStorage = new JSONStorage(window.sessionStorage);
export const localStorage = new JSONStorage(window.localStorage);

export const nullableStringSetter = (value: string | null) => (isEmpty(value) ? null : value);

// Parse and format string as big integer like 10000000 into 10.000.000
export const parseStringToInteger = (value: string | null) =>
  value ? parseInt(value.toString().replace(/\./g, '')) : null;
export const formatStringAsInteger = (value: string | null) =>
  value ? value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d)\.?)/g, '.') : '';

export const getYearFromDate = (date?: string | null) => (date ? new Date(date).getFullYear() : null);
export const getQuarterFromDate = (date?: string | null) =>
  date ? Math.ceil((new Date(date).getMonth() + 1) / 3).toFixed(0) : null;
export const displayQuarter = (value?: string | null) =>
  value ? `Q${getQuarterFromDate(value)} ${getYearFromDate(value)}` : null;
export const getDateFromQuarterAndYear = (quarter: string | null, year: number | null) =>
  quarter && year ? new Date(year, parseInt(quarter) * 3, 1).toISOString() : null;

export const fileSize = (value?: string) => (value ? `${(parseInt(value, 10) / 1024).toFixed(2)} MB` : '');

export const isId = (id?: string | null) => {
  const pattern = /^[a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12}$/;
  return id ? pattern.test(id) : false;
};

export const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    pending: 'Pending',
    rejected: 'Rejected',
    accepted: 'Accepted',
    needs_validation: 'Under review',
    internal_rejected: 'Rejected',
  };

  return labels[status] || status;
};

export const orderTechnologies = (icons: IconState[]) => {
  const technologyIconsOrder: string[] = [
    'hydrogen',
    'biogas',
    'wind',
    'solar',
    'bess',
    'hidropower',
    'datacenter',
    'topco',
  ];
  const iconsOrdered = icons.sort((a, b) => {
    const indexA = technologyIconsOrder.indexOf(a.icon);
    const indexB = technologyIconsOrder.indexOf(b.icon);
    return indexA - indexB;
  });
  return iconsOrdered;
};

export const orderTechTransactionIcons = (transactionTechs: string[]) => {
  const technologyIconsOrder: string[] = [
    'hydrogen',
    'biogas',
    'wind',
    'solar',
    'bess',
    'hidropower',
    'datacenter',
    'topco',
  ];
  const iconsOrdered = [...transactionTechs].sort((a, b) => {
    const indexA = technologyIconsOrder.indexOf(a);
    const indexB = technologyIconsOrder.indexOf(b);
    return indexA - indexB;
  });
  return iconsOrdered;
};

export function generateTicks(min: any, max: any, interval: any): number[] {
  if (interval <= 0 || max <= min) return [];

  const adjustedMax = Math.ceil(max / interval) * interval;

  const ticks: number[] = [];
  for (let value = min; value <= adjustedMax; value += interval) {
    ticks.push(value);
  }

  return ticks;
}

export function europeanFormatNumber(number?: number | null, decimals: number = 1): string {
  const num = Number(number);
  if (isNaN(num)) return '';

  const isInteger = num % 1 === 0;

  return num.toLocaleString('es-ES', {
    minimumFractionDigits: isInteger ? 0 : decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  });
}

export function formatInstalledCapacity(nominal: number, peak: number): string | null {
  const hasNominal = nominal && nominal !== 0;
  const hasPeak = peak && peak !== 0;

  if (!hasNominal && !hasPeak) return null;

  const parts = [
    hasNominal && `${europeanFormatNumber(customRound(nominal))} MWn`,
    hasPeak && `${europeanFormatNumber(customRound(peak))} MWp`,
  ].filter(Boolean);

  return parts.join(' / ');
}

export function customRound(value: number): number {
  if (value > 25 && value < 100) {
    return Math.round(value / 5) * 5;
  } else if (value >= 100) {
    return Math.round(value / 10) * 10;
  } else {
    return Math.round(value);
  }
}

export function roundEurosHa(value: number): number {
  return Math.round(value / 50) * 50;
}

// URL with optional protocol, e.g. "https://www.nteaser.com" or "linkedin.com/company/nteaser".
export const URL_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/;
export const isValidUrl = (value?: string | null) => !value || URL_PATTERN.test(value);

// Adds https:// when the protocol is missing, so links don't resolve as relative paths inside the app.
export const toAbsoluteUrl = (value?: string | null) =>
  value ? (/^https?:\/\//i.test(value) ? value : `https://${value}`) : null;

// LinkedIn URL: host must be linkedin.com or a subdomain (www., es., ...). Parsing the URL avoids
// false positives such as "linkedin.com.example.com" or "example.com/linkedin.com".
export const isLinkedInUrl = (value?: string | null) => {
  const url = toAbsoluteUrl(value);
  if (!url) return true;
  try {
    const { hostname } = new URL(url);
    return hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com');
  } catch {
    return false;
  }
};

// react-hook-form setter for URL inputs: trims, empty -> null, adds https:// if missing.
export const urlSetter = (value: string | null) => toAbsoluteUrl(nullableStringSetter(value?.trim() ?? null));
